import { Button } from '@sureco/design-system'
import {
  Collapse,
  Empty,
  Progress,
  Table,
  Tag,
} from '@surecompanies/core_components'
import { Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import { DateTimeFormats } from 'helpers/datetime'
import { isProduction } from 'modules/app-config/environment'
import React from 'react'
import {
  CensusZendeskTicketJobChangeDto,
  CensusZendeskTicketJobDto,
  CensusZendeskTicketJobResultDto,
  CensusZendeskTicketJobStatus,
} from '../../api/dto/census-zendesk-ticket-job.dto'
import {
  isActiveTicketJob,
  useCensusTicketJobsPolling,
} from '../hooks/useCensusTicketJobsPolling'

const { Link } = Typography

const STATUS_DISPLAY: Record<
  CensusZendeskTicketJobStatus,
  { color: string; label: string }
> = {
  pending: { color: 'default', label: 'Pending' },
  processing: { color: 'processing', label: 'Processing' },
  completed: { color: 'success', label: 'Completed' },
  completed_with_failures: {
    color: 'warning',
    label: 'Completed with failures',
  },
  failed: { color: 'error', label: 'Failed' },
}

const changeColumns: ColumnsType<CensusZendeskTicketJobChangeDto> = [
  { title: 'Field', dataIndex: 'field', key: 'field' },
  {
    title: 'Current',
    dataIndex: 'current',
    key: 'current',
    render: (current: string) =>
      current ? (
        <span>{current}</span>
      ) : (
        <span style={{ color: '#bbb' }}>(empty)</span>
      ),
  },
  {
    title: 'Proposed',
    dataIndex: 'proposed',
    key: 'proposed',
    render: (proposed: string) =>
      proposed ? (
        <span style={{ color: '#389e0d' }}>{proposed}</span>
      ) : (
        <span style={{ color: '#bbb' }}>(empty)</span>
      ),
  },
]

const buildResultColumns = (
  zendeskDomain: string
): ColumnsType<CensusZendeskTicketJobResultDto> => [
  {
    title: 'Employee',
    key: 'employee',
    render: (_, record) =>
      record.meta?.employeeName ??
      (record.externalId ? `Row ${record.externalId}` : '-'),
  },
  {
    title: 'Ticket',
    dataIndex: 'ticketId',
    key: 'ticketId',
    render: (ticketId?: number) =>
      ticketId ? (
        <Link
          href={`https://${zendeskDomain}/agent/tickets/${ticketId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          #{ticketId}
        </Link>
      ) : (
        '-'
      ),
  },
  {
    title: 'Outcome',
    key: 'outcome',
    render: (_, record) => {
      if (record.error) {
        return <Tag color="error">{record.error}</Tag>
      }
      if (record.ticketId) {
        return <Tag color="success">Created</Tag>
      }
      return <Tag color="processing">In progress</Tag>
    },
  },
]

export default function BackgroundTicketJobs({
  publicKey,
}: {
  publicKey: string
}) {
  const zendeskDomain = isProduction()
    ? 'sureco.zendesk.com'
    : 'surecodev.zendesk.com'
  const resultColumns = React.useMemo(
    () => buildResultColumns(zendeskDomain),
    [zendeskDomain]
  )
  const { data, isLoading, isError, refetch, isFetching } =
    useCensusTicketJobsPolling(publicKey)

  const jobs = data ?? []
  const activeCount = jobs.filter(isActiveTicketJob).length

  // Surface this section once there is at least one ticket job (processing or
  // already processed); stay hidden only when no job exists for this file.
  if (jobs.length === 0) {
    return null
  }

  const columns: ColumnsType<CensusZendeskTicketJobDto> = [
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => dayjs(createdAt).format(DateTimeFormats.f),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action?: string | null) =>
        action ? <Tag>{action}</Tag> : <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: CensusZendeskTicketJobStatus) => {
        const display = STATUS_DISPLAY[status] ?? {
          color: 'default',
          label: status,
        }
        return <Tag color={display.color}>{display.label}</Tag>
      },
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 180,
      render: (_, job) => {
        const processed = job.successCount + job.failureCount
        const percent = job.totalCount
          ? Math.round((processed / job.totalCount) * 100)
          : 0
        return (
          <Progress
            percent={percent}
            size="small"
            status={
              job.status === 'failed'
                ? 'exception'
                : isActiveTicketJob(job)
                  ? 'active'
                  : 'normal'
            }
            style={{ width: 160 }}
          />
        )
      },
    },
    {
      title: 'Total',
      dataIndex: 'totalCount',
      key: 'totalCount',
    },
    {
      title: 'Success',
      dataIndex: 'successCount',
      key: 'successCount',
      render: (successCount: number) => (
        <span style={{ color: '#389e0d' }}>{successCount}</span>
      ),
    },
    {
      title: 'Failed',
      dataIndex: 'failureCount',
      key: 'failureCount',
      render: (failureCount: number) =>
        failureCount > 0 ? (
          <span style={{ color: '#cf1322' }}>{failureCount}</span>
        ) : (
          <span>0</span>
        ),
    },
  ]

  const header = (
    <span style={{ fontWeight: 600 }}>
      Zendesk Ticket Jobs{' '}
      {activeCount > 0 ? (
        <Tag color="processing" style={{ marginLeft: 8 }}>
          {activeCount} running
        </Tag>
      ) : null}
    </span>
  )

  return (
    <div style={{ margin: '15px 15px 0 15px' }}>
      <Collapse defaultActiveKey={['ticket-jobs']}>
        <Collapse.Panel
          key="ticket-jobs"
          header={header}
          extra={
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                refetch()
              }}
              loading={isFetching}
            >
              Refresh
            </Button>
          }
        >
          {isError ? (
            <Empty description="Failed to load background ticket jobs" />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table<CensusZendeskTicketJobDto>
                rowKey="id"
                size="small"
                loading={isLoading}
                columns={columns}
                dataSource={jobs}
                pagination={false}
                scroll={{ y: 360 }}
                locale={{
                  emptyText: (
                    <Empty
                      description="No background ticket jobs yet. Approving updates will create Zendesk tickets in the background."
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
                expandable={{
                  expandedRowRender: (job) => (
                    <Table<CensusZendeskTicketJobResultDto>
                      rowKey={(_, index) => String(index)}
                      size="small"
                      columns={resultColumns}
                      dataSource={job.results}
                      pagination={false}
                      locale={{
                        emptyText: isActiveTicketJob(job)
                          ? 'Tickets are still being created...'
                          : 'No ticket results recorded for this job.',
                      }}
                      expandable={{
                        expandedRowRender: (result) => (
                          <div style={{ padding: '4px 0' }}>
                            {result.meta?.changes &&
                            result.meta.changes.length > 0 ? (
                              <Table<CensusZendeskTicketJobChangeDto>
                                rowKey="field"
                                size="small"
                                columns={changeColumns}
                                dataSource={result.meta.changes}
                                pagination={false}
                              />
                            ) : (
                              <span style={{ color: '#999' }}>
                                No field changes recorded.
                              </span>
                            )}
                            {result.payload ? (
                              <pre
                                style={{
                                  margin: '12px 0 0 0',
                                  maxHeight: 300,
                                  overflow: 'auto',
                                  fontSize: 12,
                                  background: '#f6f8fa',
                                  padding: 12,
                                  borderRadius: 6,
                                }}
                              >
                                {JSON.stringify(result.payload, null, 2)}
                              </pre>
                            ) : null}
                          </div>
                        ),
                        rowExpandable: (result) =>
                          !!result.payload ||
                          (result.meta?.changes?.length ?? 0) > 0,
                      }}
                    />
                  ),
                  rowExpandable: (job) =>
                    job.results.length > 0 || isActiveTicketJob(job),
                }}
              />
            </div>
          )}
        </Collapse.Panel>
      </Collapse>
    </div>
  )
}
